import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldCheck, Loader2, ArrowRight } from 'lucide-react';

const BTYPE_LABEL = {
  agency: 'Agencia', developer: 'Desarrollador', independent: 'Independiente',
  franchise: 'Franquicia', owner: 'Propietario', car_dealer: 'Car-Dealer',
};

/**
 * Banner para Superadmin (sin impersonar) que permite elegir una compañía y
 * abrir su configuración de IA. Al confirmar, impersona la compañía y recarga
 * la vista con el contexto correcto (vertical, allowed_modules, herencia KB,
 * skills) — exactamente igual que el flujo de impersonación tradicional.
 */
export const SuperadminAICompanySelector = ({ targetLabel = 'IA' }) => {
  const { impersonate } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);
  const [entering, setEntering] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await api.get('/admin/companies');
        if (active) setCompanies(r.data || []);
      } catch {
        if (active) setCompanies([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const enterCompany = async () => {
    if (!selected) {
      toast.error('Selecciona una compañía');
      return;
    }
    setEntering(true);
    try {
      await impersonate(selected);
      const name = companies.find((c) => c.id === selected)?.name || 'la compañía';
      toast.success(`Configurando ${targetLabel} de ${name}`);
      // Recarga limpia con el token impersonado → contexto/vertical correcto.
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo acceder a la compañía');
      setEntering(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 sm:p-5"
      data-testid="superadmin-ai-company-selector"
    >
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-100">Estás como Superadmin</p>
          <p className="text-[12px] text-amber-200/80 mt-1">
            Selecciona una compañía para configurar su <span className="font-semibold">{targetLabel}</span>.
            Los cambios se guardarán en esa compañía y la interfaz respetará su vertical y módulos.
          </p>
          <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="flex-1 min-w-0 max-w-md">
              <Select value={selected} onValueChange={setSelected} disabled={loading || entering}>
                <SelectTrigger data-testid="superadmin-ai-company-select" className="bg-black/30 border-white/10 text-xs">
                  <SelectValue placeholder={loading ? 'Cargando compañías…' : 'Elige una compañía…'} />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`superadmin-ai-company-option-${c.id}`}>
                      {c.name} · {BTYPE_LABEL[c.business_type] || c.business_type || 'Agencia'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={enterCompany}
              disabled={entering || loading || !selected}
              className="bg-amber-600 hover:bg-amber-500 text-white text-xs shrink-0"
              data-testid="superadmin-ai-company-enter-button"
            >
              {entering ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5 mr-1" />}
              Configurar esta compañía
            </Button>
          </div>
          <p className="text-[11px] text-amber-200/60 mt-2">
            También puedes hacerlo desde BackOffice → Cuentas, con los botones de acceso directo a IA por compañía.
          </p>
        </div>
      </div>
    </div>
  );
};
